import { Placeholder } from "@tiptap/extensions";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { useEffect, useRef, useState } from "react";
import { Markdown } from "tiptap-markdown";
import "./comment-composer.scss";
import { BoardAiApi } from "../../../services/board-ai-api";
import AiSparkleIcon from "./ai-sparkle-icon";
import { BrandAutocorrect } from "./brand-autocorrect";
import { promptDialog } from "./confirm-service";
import EditorLinkMenu from "./editor-link-menu";
import EmojiPicker from "./emoji-picker";
import { IssueKeyDecoration } from "./issue-key-decoration";
import { MentionDecoration } from "./mention-decoration";
import { useMentionSuggest } from "./mention-suggest";
import { useBoardAiEnabled } from "./use-board-ai";

interface CommentEditorProps {
  candidates: string[];
  // Receives the composed Markdown. The editor clears itself afterwards (compose mode).
  onSubmit: (markdown: string) => void;
  placeholder?: string;
  // Edit mode: seed the editor and show Cancel + a custom submit label.
  initialMarkdown?: string;
  submitLabel?: string;
  onCancel?: () => void;
  autoFocus?: boolean;
  // Real project keys; bare issue keys (KUI-12) typed before a delimiter become clickable chips.
  issueKeys?: Set<string>;
  onIssueKey?: (key: string) => void;
}

interface CommentToolButtonProps {
  label: React.ReactNode;
  title: string;
  active?: boolean;
  onRun: () => void;
}

// Reuses the description editor's toolbar styling (flat `.kuika-md-editor__tool*` classes) so the
// comment composer gets the same Jira/Linear-style WYSIWYG controls. mousedown is prevented so the
// editor keeps its selection while the command runs.
const CommentToolButton: React.FC<CommentToolButtonProps> = ({ label, title, active, onRun }) => (
  <button
    type="button"
    className={`kuika-md-editor__tool${active ? " kuika-md-editor__tool--active" : ""}`}
    title={title}
    aria-label={title}
    aria-pressed={active}
    onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
    onClick={onRun}
  >
    {label}
  </button>
);

const CommentEditor: React.FC<CommentEditorProps> = ({
  candidates,
  onSubmit,
  placeholder,
  initialMarkdown,
  submitLabel,
  onCancel,
  autoFocus,
  issueKeys,
  onIssueKey
}) => {
  const mentions = useMentionSuggest(candidates);
  // Latest member names for the mention-chip decoration, read through a ref.
  const mentionNamesRef = useRef<string[]>(candidates);
  mentionNamesRef.current = candidates;
  const [emojiOpen, setEmojiOpen] = useState<boolean>(false);
  const [focused, setFocused] = useState<boolean>(false);
  const aiEnabled: boolean = useBoardAiEnabled();
  const [aiBusy, setAiBusy] = useState<boolean>(false);

  // Rewrite the current draft to read better, preserving meaning and Markdown. No-op while empty
  // or already running. The rewritten text replaces the editor content (parsed back to rich text
  // by tiptap-markdown's setContent).
  const aiRewrite = (instance: Editor): void => {
    const markdown: string = instance.storage.markdown.getMarkdown().trim();
    if (!markdown || aiBusy) return;
    setAiBusy(true);
    void BoardAiApi.commentAssist({ mode: "rewrite", text: markdown })
      .then((result): void => {
        instance.chain().focus().setContent(result.text).run();
      })
      .catch((): void => undefined)
      .finally((): void => setAiBusy(false));
  };

  const submit = (instance: Editor): void => {
    const markdown: string = instance.storage.markdown.getMarkdown().trim();
    if (markdown.length === 0) return;
    onSubmit(markdown);
    instance.commands.clearContent();
  };

  const setLink = (instance: Editor): void => {
    const previous: string = (instance.getAttributes("link").href as string) ?? "";
    void promptDialog({
      title: "Link",
      label: "URL",
      placeholder: "https://",
      defaultValue: previous,
      confirmLabel: "Apply",
      allowEmpty: true,
      intent: "create",
      iconName: "link"
    }).then((url: string | null) => {
      if (url === null) return;
      if (url === "") {
        instance.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      instance.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    });
  };

  const insertEmoji = (emoji: string): void => {
    editor?.chain().focus().insertContent(emoji).run();
    setEmojiOpen(false);
  };

  const editor: Editor | null = useEditor({
    extensions: [
      // Link ships inside StarterKit since tiptap 3, so it is configured here rather than
      // added as a second extension (two instances of one extension is an error).
      StarterKit.configure({
        heading: { levels: [3] },
        link: {
          openOnClick: true,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" }
        }
      }),
      Placeholder.configure({ placeholder: placeholder ?? "Leave a comment…" }),
      Markdown.configure({ html: false, linkify: true, transformPastedText: true, transformCopiedText: true }),
      IssueKeyDecoration.configure({ projectKeys: issueKeys ?? new Set<string>() }),
      MentionDecoration.configure({ getMemberNames: () => mentionNamesRef.current }),
      BrandAutocorrect
    ],
    content: initialMarkdown ?? "",
    autofocus: autoFocus ? "end" : false,
    // tiptap 3 stopped re-rendering the React tree on every transaction, and the toolbar reads
    // `editor.isActive(...)` during render for its pressed states.
    shouldRerenderOnTransaction: true,
    editorProps: {
      attributes: { class: "kuika-md-field__prose kuika-comment-editor__content" },
      handleKeyDown: (_view, event: KeyboardEvent): boolean => {
        if (editorRef.current && mentions.handleKeyDown(editorRef.current, event)) return true;
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          if (editorRef.current) submit(editorRef.current);
          return true;
        }
        return false;
      }
    },
    onUpdate: ({ editor: instance }) => mentions.sync(instance),
    onSelectionUpdate: ({ editor: instance }) => mentions.sync(instance),
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false)
  });

  // Stable handle to the editor for the (creation-time) key handler closure.
  const editorRef = useRef<Editor | null>(null);
  editorRef.current = editor;

  // Re-scan for mention chips when the member list loads/changes (empty transaction drives the
  // MentionDecoration's apply).
  useEffect(() => {
    if (editor) editor.view.dispatch(editor.state.tr);
  }, [editor, candidates]);

  // Active states (bold/list/…) reflect the caret's marks, which persist on blur — only surface
  // them while editing, else a blurred composer lights buttons based on the last caret position.
  const activeFor = (name: string): boolean => focused && (editor?.isActive(name) ?? false);

  // Close the emoji popover on an outside click.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!emojiOpen) return;
    const onDown = (event: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [emojiOpen]);

  // A click on a promoted issue-key chip opens that issue (the decoration carries the key).
  const handleChipClick = (event: React.MouseEvent): void => {
    if (!onIssueKey) return;
    const target: HTMLElement | null = event.target as HTMLElement | null;
    const chip: HTMLElement | null =
      (target?.closest?.(".kuika-board__issue-chip[data-issue-key]") as HTMLElement | null) ?? null;
    const key: string | null = chip?.getAttribute("data-issue-key") ?? null;
    if (key) {
      event.preventDefault();
      onIssueKey(key);
    }
  };

  return (
    <div className="kuika-comment-composer kuika-comment-editor" ref={wrapperRef} onClick={handleChipClick}>
      <div className="kuika-comment-editor__box">
        {editor && (
          <div className="kuika-md-editor__toolbar">
            <CommentToolButton
              label={<strong>B</strong>}
              title="Bold  (⌘B)"
              active={activeFor("bold")}
              onRun={() => editor.chain().focus().toggleBold().run()}
            />
            <CommentToolButton
              label={<em>I</em>}
              title="Italic  (⌘I)"
              active={activeFor("italic")}
              onRun={() => editor.chain().focus().toggleItalic().run()}
            />
            <CommentToolButton
              label={<s>S</s>}
              title="Strikethrough"
              active={activeFor("strike")}
              onRun={() => editor.chain().focus().toggleStrike().run()}
            />
            <CommentToolButton
              label={<>&lt;/&gt;</>}
              title="Inline code"
              active={activeFor("code")}
              onRun={() => editor.chain().focus().toggleCode().run()}
            />
            <span className="kuika-md-editor__tool-sep" />
            <CommentToolButton
              label="•"
              title="Bullet list"
              active={activeFor("bulletList")}
              onRun={() => editor.chain().focus().toggleBulletList().run()}
            />
            <CommentToolButton
              label="1."
              title="Numbered list"
              active={activeFor("orderedList")}
              onRun={() => editor.chain().focus().toggleOrderedList().run()}
            />
            <CommentToolButton
              label="❝"
              title="Quote"
              active={activeFor("blockquote")}
              onRun={() => editor.chain().focus().toggleBlockquote().run()}
            />
            <CommentToolButton
              label={<>{"{ }"}</>}
              title="Code block"
              active={activeFor("codeBlock")}
              onRun={() => editor.chain().focus().toggleCodeBlock().run()}
            />
            <span className="kuika-md-editor__tool-sep" />
            <CommentToolButton
              label={
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path
                    d="M6.7 8.7a2.5 2.5 0 0 0 3.5 0l1-1a2.5 2.5 0 1 0-3.5-3.5l-.6.6"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9.3 7.3a2.5 2.5 0 0 0-3.5 0l-1 1a2.5 2.5 0 1 0 3.5 3.5l.6-.6"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              title="Link"
              active={activeFor("link")}
              onRun={() => setLink(editor)}
            />
            <CommentToolButton
              label={
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <circle cx="8" cy="8" r="5.75" stroke="currentColor" strokeWidth="1.2" />
                  <circle cx="6" cy="6.9" r="0.85" fill="currentColor" />
                  <circle cx="10" cy="6.9" r="0.85" fill="currentColor" />
                  <path
                    d="M5.5 9.6a3 3 0 0 0 5 0"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              title="Emoji"
              active={emojiOpen}
              onRun={() => setEmojiOpen((open: boolean) => !open)}
            />
            {aiEnabled && (
              <>
                <span className="kuika-md-editor__tool-sep" />
                <CommentToolButton
                  label={<AiSparkleIcon size={14} className={aiBusy ? "kuika-board__ai-spin" : undefined} />}
                  title="Improve writing with AI"
                  active={aiBusy}
                  onRun={() => editor && aiRewrite(editor)}
                />
              </>
            )}
          </div>
        )}
        {emojiOpen && (
          <div className="kuika-md-editor__emoji-pop">
            <EmojiPicker onPick={insertEmoji} />
          </div>
        )}
        <div className="kuika-comment-editor__surface">
          <EditorContent editor={editor} />
        </div>
      </div>

      {mentions.popup(editor)}

      <div className="kuika-comment-composer__footer">
        <span className="kuika-comment-composer__hint">@ to mention · ⌘↵ to send</span>
        <div className="kuika-comment-editor__actions">
          {onCancel && (
            <button type="button" className="kuika-comment-editor__cancel" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button type="button" className="kuika-comment-composer__send" onClick={() => editor && submit(editor)}>
            {submitLabel ?? "Comment"}
          </button>
        </div>
      </div>
      <EditorLinkMenu editor={editor} />
    </div>
  );
};

export default CommentEditor;
